import { Link } from "react-router-dom";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import Reveal from "../../components/Reveal";

const FEATURES = [
  ["Voice Guide", "Narrates every element the student hovers over or tabs through (buttons, links, forms, headings) so they can navigate without seeing clearly."],
  ["Read Aloud", "Highlight any text and hear it read at the student's preferred voice and speed."],
  ["Zone Tracking", "Announces where the mouse went when it leaves the page (address bar, back button, window controls) so a student with low vision always knows where they are."],
  ["High Contrast", "Dark mode that makes text and colour much easier to distinguish."],
  ["Text Scaling", "Scales text from 80% to 150% across any page with a single slider."],
  ["Reading Space", "Increases line height, letter spacing, and word spacing for students who struggle with dense text."],
  ["Content Cleaner", "Strips adverts, navigation bars, sidebars, and cookie banners so only the core content remains."],
  ["Right-Click Menu", "Every feature accessible without opening the popup, useful for students with limited motor control."],
  ["Keyboard Shortcuts", "Alt+Shift+D describes the focused element; Alt+Shift+R toggles the voice guide on/off."],
  ["Settings Page", "Students set defaults once (voice speed, pitch, text size, preferred helpers) and the extension remembers them across sessions."],
];

const ROADMAP = [
  ["Ghanaian & African Language TTS", "Twi, Ga, Dagbani, Hausa, Ewe. Students learn better when they hear instructions in the language they grew up with. Extablify intends to integrate models from GhanaNLP and Masakhane as they become production-ready."],
  ["Local Language Translation", "Translate web content into local Ghanaian and broader African languages in-browser, enabling students to read academic materials in their first language without leaving the page."],
  ["Page-Aware Reading for School Platforms", "GES portals, WAEC result checkers, BECE and WASSCE prep sites: trained to give context-aware narration specific to the structures of Ghanaian educational platforms."],
  ["Audio-Guided Form & Assessment Assistance", "Talks a student through every form field, confirms answers before submission, and alerts to errors in plain, student-friendly language."],
  ["Offline-First, Low-Bandwidth Architecture", "Ghana's network connectivity is uneven. The final version must work fully offline: voices cached locally, settings synced when connectivity returns."],
];

export default function ProjectExtablify() {
  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pt-32 md:px-12 md:pt-40">
        <Link to="/projects" className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint hover:text-heading">
          &larr; Back to Projects
        </Link>
      </div>

      <Reveal className="mx-auto max-w-content px-6 pb-14 pt-8 md:px-12 md:pb-20">
        <p className="eyebrow">Chrome Extension &middot; Accessibility &middot; Ed-Tech &middot; Open Source</p>
        <h1 className="mt-3 font-serif text-4xl font-extrabold leading-tight text-heading md:text-5xl">
          Extab<em className="italic text-gold-500">lify</em>
        </h1>
        <p className="mt-6 max-w-[62ch] text-[1rem] leading-relaxed text-ink-soft">
          A free, open-source browser extension built so that <strong className="font-medium text-heading">every
          student in Ghana can navigate the web on their own terms</strong>, regardless of visual impairment,
          dyslexia, low vision, or reading difficulty. No account. No server. Fully offline. Built for the classroom.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <a
            href="https://www.extablify.com"
            target="_blank"
            rel="noreferrer"
            className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            Live Site
          </a>
          <a
            href="https://github.com/ellenboakye/extablify"
            target="_blank"
            rel="noreferrer"
            className="text-[0.75rem] uppercase tracking-widest2 text-ink-soft hover:text-heading"
          >
            GitHub
          </a>
          <a href="#install" className="text-[0.75rem] uppercase tracking-widest2 text-ink-soft hover:text-heading">
            How to install &darr;
          </a>
        </div>
        <div className="mt-6 h-[3px] max-w-xs bg-gradient-to-r from-forest-700 via-gold-500 to-forest-700" aria-hidden="true" />
      </Reveal>

      <Reveal as="section" className="border-y border-line bg-surface-alt">
        <div className="mx-auto grid max-w-content grid-cols-2 divide-x divide-line px-6 sm:grid-cols-4 md:px-12">
          <div className="px-2 py-8 text-center first:pl-0 sm:px-6">
            <div className="font-serif text-3xl text-heading">10</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Accessibility Features</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">0</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Data Leaves Device</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">MIT</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Open Source Licence</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">🇬🇭</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Built for Ghana</div>
          </div>
        </div>
      </Reveal>

      <section className="border-b border-line">
        <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-20">
          <p className="eyebrow">Project Walkthrough</p>
          <h2 className="mt-2 font-serif text-2xl text-heading">See it in action</h2>
          <div className="mt-8 aspect-video border border-line bg-surface-alt">
            <iframe
              className="h-full w-full"
              src="https://www.youtube.com/embed/VA9eKp58XSc"
              title="Extablify walkthrough"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
        <div className="grid gap-14 md:grid-cols-[1fr_320px] md:gap-16">
          <div>
            <Reveal className="mb-16">
              <p className="eyebrow">Overview</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">Why this exists</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Ghana has had a disability law since 2006. The Ghana Education Service has a Special Education
                Division. Schools for the blind, deaf, and students with learning differences exist in every
                region. And yet, when a student with visual impairment sits down at a computer in a school lab,
                at GISE, at Wa School for the Blind, at Akropong, at any JHS or SHS trying to include students
                with different needs, the web does not speak to them.
              </p>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Most assistive technology was built for English-speaking, Western markets. NVDA and JAWS are
                powerful but cost money, require Windows, and speak no Ghanaian language. Extablify starts
                filling that gap today, with what is already possible in a browser.
              </p>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">Features</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">What it does right now</h2>
              <div className="mt-6 divide-y divide-line border-t border-line">
                {FEATURES.map(([title, desc]) => (
                  <div key={title} className="grid gap-1 py-5 md:grid-cols-[200px_1fr] md:gap-8">
                    <p className="font-serif text-base text-heading">{title}</p>
                    <p className="text-[0.9rem] leading-relaxed text-ink-soft">{desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">What&rsquo;s Next</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">The bigger vision</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                The current version is the foundation. The mission it is building toward is an
                African-language-powered screen reader and learning companion, free, open source, and built
                for the classroom.
              </p>
              <div className="mt-6 space-y-6">
                {ROADMAP.map(([title, desc], i) => (
                  <div key={title} className="flex gap-5 border-t border-line pt-5">
                    <span className="font-serif text-lg text-ink-faint">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="font-serif text-base text-heading">{title}</p>
                      <p className="mt-1 text-[0.9rem] leading-relaxed text-ink-soft">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal id="install">
              <p className="eyebrow">Getting Started</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">How to install it</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Not yet on the Chrome Web Store, this is active early development. Load it directly in Chrome
                or any Chromium-based browser (including Microsoft Edge):
              </p>
              <ol className="mt-6 space-y-4">
                {[
                  "Download or clone the repository to your computer.",
                  <>Open Chrome and navigate to <code className="rounded bg-gold-300/20 px-1.5 py-0.5 text-[0.85em] text-gold-700">chrome://extensions</code></>,
                  <>Enable <strong className="font-medium text-heading">Developer mode</strong> using the toggle in the top right.</>,
                  <>Click <strong className="font-medium text-heading">Load unpacked</strong> and select the Extablify folder.</>,
                  "The Extablify icon will appear in your browser toolbar, click it to start.",
                ].map((step, i) => (
                  <li key={i} className="flex gap-4 border-t border-line pt-4 text-[0.9rem] leading-relaxed text-ink-soft">
                    <span className="font-serif text-lg text-gold-500">{i + 1}</span>
                    <span className="pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>

          <Reveal as="aside" className="h-fit space-y-6 md:sticky md:top-28">
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Tech Stack</p>
              <p className="text-[0.85rem] leading-relaxed text-ink-soft">
                HTML &middot; CSS &middot; JavaScript &middot; Chrome MV3 &middot; Web Speech API
              </p>
            </div>
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Project Info</p>
              <dl className="space-y-3 text-[0.85rem]">
                <div><dt className="text-ink-faint">Type</dt><dd className="text-heading">Browser Extension</dd></div>
                <div><dt className="text-ink-faint">Licence</dt><dd className="text-heading">MIT: free to use, modify &amp; redistribute</dd></div>
                <div><dt className="text-ink-faint">Status</dt><dd className="text-heading">Active Development</dd></div>
                <div><dt className="text-ink-faint">Target</dt><dd className="text-heading">Ghanaian schools &amp; students</dd></div>
              </dl>
            </div>
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">APIs &amp; Browser</p>
              <p className="text-[0.85rem] leading-relaxed text-ink-soft">
                Manifest V3 &middot; Web Speech API &middot; Storage API &middot; Context Menus API
              </p>
            </div>
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Links</p>
              <a href="https://www.extablify.com" target="_blank" rel="noreferrer" className="block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading">
                extablify.com &rarr;
              </a>
              <a href="https://github.com/ellenboakye/extablify" target="_blank" rel="noreferrer" className="mt-3 block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading">
                View on GitHub &rarr;
              </a>
              <a href="#install" className="mt-3 block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading">
                Install instructions &darr;
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal as="section" className="bg-forest-900">
        <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
          <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">Why it matters</p>
          <h2 className="mt-2 font-serif text-2xl font-extrabold text-white md:text-3xl">
            The impact this seeks to create
          </h2>
          <div className="mt-10 grid gap-px border-y border-white/15 sm:grid-cols-3">
            <div className="border-t border-white/15 py-6 pr-6 sm:border-t-0">
              <div className="font-serif text-2xl text-white">3M+</div>
              <p className="mt-2 text-[0.85rem] leading-relaxed text-forest-50/75">
                People with disabilities in Ghana, educational exclusion begins early and hits the hardest in digital spaces.
              </p>
            </div>
            <div className="border-t border-white/15 py-6 sm:border-t-0 sm:px-6">
              <div className="font-serif text-2xl text-white">₵0</div>
              <p className="mt-2 text-[0.85rem] leading-relaxed text-forest-50/75">
                Cost to deploy in any school. No training budget, no IT infrastructure, no account creation required.
              </p>
            </div>
            <div className="border-t border-white/15 py-6 pl-0 sm:border-t-0 sm:pl-6">
              <div className="font-serif text-2xl text-white">Pan-African</div>
              <p className="mt-2 text-[0.85rem] leading-relaxed text-forest-50/75">
                The same architecture that works for Ghanaian students in Twi and English can extend to Yoruba, Amharic, Swahili, Zulu, and beyond.
              </p>
            </div>
          </div>
        </div>
      </Reveal>

      <section className="border-t border-line">
        <div className="mx-auto flex max-w-content flex-wrap items-center justify-between gap-6 px-6 py-14 md:px-12">
          <p className="font-serif text-xl text-heading">See all projects</p>
          <Link
            to="/projects"
            className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            Browse Projects &rarr;
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
